/**
 * Ids in batches a route takes, each id once, in the order first given:
 * the fit route scores 50 postings a call, the applications route reads
 * 200. A page that shows more asks in more calls rather than dropping
 * the rest.
 */
export function batches<Item>(items: readonly Item[], size: number): Item[][] {
    // A size that is not a whole number would drop every id (NaN, Infinity)
    // or overfill a batch (2.5).
    if (!Number.isSafeInteger(size) || size < 1) {
        throw new RangeError("a batch holds a whole number of ids, one at least");
    }
    const unique = [...new Set(items)];
    return Array.from({ length: Math.ceil(unique.length / size) }, (_, index) =>
        unique.slice(index * size, index * size + size),
    );
}

/** What several batched reads add up to, read as one. */
export interface Batched<Value> {
    /** Every batch's answer merged; undefined until one has answered. */
    data: Value | undefined;
    /** Whether any batch is still being read. */
    isPending: boolean;
    /** Whether every batch has answered, and there was one at least. */
    isSuccess: boolean;
    /** Whether any batch failed. */
    isError: boolean;
    error: unknown;
}

/**
 * Merges batched reads keyed by id. `null` from any batch (the fit route
 * with no resume to score) is null for the whole, since it is the same
 * answer for every id.
 */
export function mergeBatches<Entry>(
    results: readonly {
        data: Record<string, Entry> | null | undefined;
        isPending: boolean;
        isSuccess: boolean;
        isError: boolean;
        error: unknown;
    }[],
): Batched<Record<string, Entry> | null> {
    const answered = results.filter((result) => result.data !== undefined);
    const isNull = answered.some((result) => result.data === null);
    const failed = results.find((result) => result.isError);
    return {
        data: isNull
            ? null
            : answered.length === 0
              ? undefined
              : Object.assign({}, ...answered.map((result) => result.data)),
        isPending: results.some((result) => result.isPending),
        isSuccess: results.length > 0 && results.every((result) => result.isSuccess),
        isError: Boolean(failed),
        error: failed?.error ?? null,
    };
}

/** The same, for a route that never answers null: no answer yet is undefined. */
export function mergeRecords<Entry>(
    results: Parameters<typeof mergeBatches<Entry>>[0],
): Batched<Record<string, Entry>> {
    const merged = mergeBatches(results);
    return { ...merged, data: merged.data ?? undefined };
}
